import {
  formatPostalCode,
  type ConversationDetail,
  type JobLocation,
  type ProposalStatus,
  type WantedStart,
} from '@hemmafixa/shared';
import { Avatar, Button, Card, DescriptionList, StatusBadge } from '@hemmafixa/ui';
import type { ComponentProps } from 'react';
import { Link } from 'react-router';
import { useI18n } from '@/i18n/context';
import { useJobProposals, useMyJob } from '../client/api';
import { useOpenJob } from '../provider/api';
import { useCategoryLabel } from '../shared/catalogue';
import { ContactDetailsList } from '../shared/contact-details';
import { JobLocationMap } from '../shared/job-location-map';
import { JobFiles } from '../shared/job-files';
import { useStatuses } from '../shared/statuses';

interface JobFacts {
  id: string;
  title: string;
  categorySlug: string;
  postalCode: string;
  wantedStart: WantedStart;
  location: JobLocation | null;
  description: string;
  attachments: ComponentProps<typeof JobFiles>['files'];
}

/**
 * Beside an open chat (UI-07): who the other side is with their contact details, the job and the agreed
 * proposal, so nobody has to leave the conversation to look something up.
 */
export function ConversationInfo({ detail }: { detail: ConversationDetail }) {
  const { t } = useI18n();
  const client = detail.side === 'client';
  return (
    <>
      <Card className="grid gap-3 p-4">
        <div className="flex items-center gap-3">
          <Avatar
            seed={client ? detail.providerId : undefined}
            name={detail.otherName}
            kind={client ? 'company' : 'person'}
            size="lg"
          />
          <div className="grid min-w-0 gap-0.5">
            <p className="truncate font-semibold">{detail.otherName}</p>
            <p className="text-xs text-muted-foreground">{client ? t.areas.provider : t.areas.client}</p>
          </div>
        </div>
        <ContactDetailsList contact={detail.contact} />
        {client && (
          <Button asChild variant="outline" size="sm" className="justify-self-start">
            <Link to={`/client/jobs/providers/${detail.providerId}`}>{t.client.job.viewProfile}</Link>
          </Button>
        )}
      </Card>
      {client ? <ClientJobInfo detail={detail} /> : <ProviderJobInfo detail={detail} />}
    </>
  );
}

function ClientJobInfo({ detail }: { detail: ConversationDetail }) {
  const job = useMyJob(detail.job.id);
  const proposals = useJobProposals(detail.job.id);
  const proposal = proposals.data?.find((item) => item.id === detail.proposalId);
  return <JobCard job={job.data ?? undefined} side="client" proposal={proposal} />;
}

function ProviderJobInfo({ detail }: { detail: ConversationDetail }) {
  const job = useOpenJob(detail.job.id);
  const data = job.data ?? undefined;
  return <JobCard job={data} side="provider" proposal={data?.myProposal ?? undefined} />;
}

interface ProposalFacts {
  status: ProposalStatus;
  priceFromOre: number;
  priceToOre: number | null;
}

function JobCard({
  job,
  side,
  proposal,
}: {
  job?: JobFacts;
  side: 'client' | 'provider';
  proposal?: ProposalFacts;
}) {
  const { t, money } = useI18n();
  const categoryLabel = useCategoryLabel();
  const statuses = useStatuses();
  if (!job) return null;
  return (
    <Card className="grid gap-3 p-4">
      <div className="grid gap-0.5">
        <p className="text-xs font-medium text-muted-foreground">{t.job.details}</p>
        <Link to={`/${side}/jobs/${job.id}`} className="font-semibold underline-offset-4 hover:underline">
          {job.title}
        </Link>
      </div>
      {proposal && (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-muted px-3 py-2">
          <span className="text-sm font-bold tabular-nums">
            {money(proposal.priceFromOre)}
            {proposal.priceToOre ? ` – ${money(proposal.priceToOre)}` : ''}
          </span>
          <StatusBadge status={proposal.status} statuses={statuses.proposal} />
        </div>
      )}
      <DescriptionList
        className="grid-cols-2"
        items={[
          { label: t.job.category, value: categoryLabel(job.categorySlug) },
          { label: t.job.location, value: formatPostalCode(job.postalCode) },
          { label: t.job.wantedStart, value: t.wantedStart[job.wantedStart] },
        ]}
      />
      <p className="line-clamp-6 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
        {job.description}
      </p>
      <JobLocationMap location={job.location} postalCode={job.postalCode} className="h-36" />
      {job.attachments.length > 0 && <JobFiles files={job.attachments} />}
    </Card>
  );
}
